'use client';

import { useEffect, useMemo, useState } from 'react';

import { applyAnalyticsConsent } from '@/lib/ga';
import type { ConsentState } from '@/lib/types';

const STORAGE_KEY = 'bpz-consent-v1';

function defaultConsent(): ConsentState {
  return {
    necessary: true,
    analytics: false,
    marketing: false,
    updatedAt: new Date().toISOString(),
  };
}

function readConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.marketing !== 'boolean') {
      return null;
    }
    return {
      necessary: true,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      updatedAt: parsed.updatedAt || new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

function persistConsent(consent: ConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
}

export function ConsentManager() {
  const [consent, setConsent] = useState<ConsentState | null>(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [draftAnalytics, setDraftAnalytics] = useState(false);
  const [draftMarketing, setDraftMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (existing) {
      setConsent(existing);
      setDraftAnalytics(existing.analytics);
      setDraftMarketing(existing.marketing);
      setBannerVisible(false);
    } else {
      const fresh = defaultConsent();
      setConsent(fresh);
      setDraftAnalytics(false);
      setDraftMarketing(false);
      setBannerVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!consent) return;
    applyAnalyticsConsent(consent.analytics);
  }, [consent]);

  useEffect(() => {
    (window as any).__bpzOpenConsent = () => {
      setPanelOpen(true);
    };
    return () => {
      delete (window as any).__bpzOpenConsent;
    };
  }, []);

  const canSave = useMemo(() => consent !== null, [consent]);

  const save = (analytics: boolean, marketing: boolean) => {
    const next: ConsentState = {
      necessary: true,
      analytics,
      marketing,
      updatedAt: new Date().toISOString(),
    };
    setConsent(next);
    persistConsent(next);
    setBannerVisible(false);
    setPanelOpen(false);
  };

  if (!consent) return null;

  return (
    <>
      {bannerVisible && (
        <div className="consent-banner" role="dialog" aria-label="Cookie consent banner">
          <p>
            We use essential cookies and optional analytics cookies. Analytics stays off until you consent.
          </p>
          <div className="consent-actions">
            <button className="btn secondary" type="button" onClick={() => setPanelOpen(true)}>
              Manage
            </button>
            <button className="btn secondary" type="button" onClick={() => save(false, false)}>
              Reject non-essential
            </button>
            <button className="btn primary" type="button" onClick={() => save(true, false)}>
              Accept analytics
            </button>
          </div>
        </div>
      )}

      {panelOpen && (
        <div className="consent-modal-backdrop" role="dialog" aria-modal="true" aria-label="Cookie preferences">
          <div className="consent-modal">
            <h3>Cookie preferences</h3>
            <label className="consent-row">
              <input type="checkbox" checked disabled />
              <span>
                <strong>Necessary</strong>
                <small>Required for basic website functionality.</small>
              </span>
            </label>
            <label className="consent-row">
              <input
                type="checkbox"
                checked={draftAnalytics}
                onChange={(e) => setDraftAnalytics(e.target.checked)}
              />
              <span>
                <strong>Analytics (Google Analytics)</strong>
                <small>Helps us measure traffic and improve UX.</small>
              </span>
            </label>
            <label className="consent-row">
              <input
                type="checkbox"
                checked={draftMarketing}
                onChange={(e) => setDraftMarketing(e.target.checked)}
              />
              <span>
                <strong>Marketing</strong>
                <small>Reserved for future use. Not used in MVP.</small>
              </span>
            </label>

            <div className="consent-actions">
              <button className="btn secondary" type="button" onClick={() => setPanelOpen(false)}>
                Cancel
              </button>
              <button className="btn primary" type="button" disabled={!canSave} onClick={() => save(draftAnalytics, draftMarketing)}>
                Save preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
